export function startScatterNetwork(container) {
    let canvas = container.querySelector('canvas.scatter-network');
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'scatter-network';
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.pointerEvents = 'none';
        canvas.style.border = 'none';
        canvas.style.outline = 'none';
        canvas.style.zIndex = '0'; // Behind dots but above background
        container.style.position = 'relative'; // Ensure container is relative
        container.style.border = 'none';
        container.style.outline = 'none';
        container.insertBefore(canvas, container.firstChild);
    }

    const ctx = canvas.getContext('2d');
    let animationId;

    function resize() {
        const rect = container.getBoundingClientRect();
        canvas.width = rect.width || 300;
        canvas.height = rect.height || 300;
    }
    resize();
    window.addEventListener('resize', resize);

    // Wave Configuration
    const WAVE_WAIT_MIN = 5000; // Minimum time between waves (ms)
    const WAVE_WAIT_MAX = 7000; // Maximum time between waves (ms)
    const WAVE_DURATION_MIN = 1000; // Minimum duration of a wave (ms)
    const WAVE_DURATION_MAX = 4000; // Maximum duration of a wave (ms)
    const WAVE_INTENSITY = 0.74; // Multiplier for wave visual effects (connections, point sizes, opacity boosts)
    const WAVE_SECONDARY_CHANCE = 0.3; // Chance (0.0 to 1.0) of a wave being immediately followed by a secondary half-intensity wave
    const waveBandWidth = 250; // The width of the wave effect band in pixels

    // Grid Animation Configuration
    const SHOW_GRID_ANIMATION = true; // 1. Show the grid animation or not
    const GRID_ANIMATION_SPEED = 5000; // 2. The speed of the grid animation (ms)
    const GRID_ANIMATION_INTERVAL_MIN = 4000; // Minimum duration between showing the grid animation (ms)
    const GRID_ANIMATION_INTERVAL_MAX = 15000; // Maximum duration between showing the grid animation (ms)
    // Clear Animation Configuration
    const CLEAR_DURATION = 2000; // Duration to naturally fade out dots when clearing (ms)
    // Wave state
    let isWaveActive = false;
    // Start initial wave halfway to minimum to get movement going early
    let nextWaveTime = Date.now() + (WAVE_WAIT_MIN / 2) + (Math.random() * (WAVE_WAIT_MAX - WAVE_WAIT_MIN));
    let waveStartTime = 0;
    let waveDuration = 0;
    let waveX = -1000;
    let waveDirection = 1; // 1 for left-to-right, -1 for right-to-left
    let currentWaveIntensity = WAVE_INTENSITY;
    let pendingSecondaryWave = false;

    // Clear state
    let isClearing = false;
    let clearStartTime = 0;
    
    // Attach clear function to window
    window.clearScatterNetwork = function() {
        if (!isClearing) {
            isClearing = true;
            clearStartTime = Date.now();
            isWaveActive = false; // Immediately stop wave animations
            isGridAnimating = false; // Instantly halt grid wiping
        }
    };

    // Grid animation state
    let isGridAnimating = false;
    let nextGridAnimTime = Date.now() + GRID_ANIMATION_INTERVAL_MIN + (Math.random() * (GRID_ANIMATION_INTERVAL_MAX - GRID_ANIMATION_INTERVAL_MIN));
    let gridAnimStartTime = 0;

    function drawGridAnimation(now) {
        if (!SHOW_GRID_ANIMATION) return;

        if (!isGridAnimating && now > nextGridAnimTime) {
            isGridAnimating = true;
            gridAnimStartTime = now;
        }

        if (isGridAnimating) {
            let progress = (now - gridAnimStartTime) / GRID_ANIMATION_SPEED;
            if (progress >= 1) {
                isGridAnimating = false;
                nextGridAnimTime = now + GRID_ANIMATION_INTERVAL_MIN + (Math.random() * (GRID_ANIMATION_INTERVAL_MAX - GRID_ANIMATION_INTERVAL_MIN));
                return; // Finished
            }

            const cols = 16;
            const rows = 8;
            const cellWidth = canvas.width / cols;
            const cellHeight = canvas.height / rows;

            // Wave moves vertically downwards, leaving fading grid behind it
            const waveCenterY = progress * (canvas.height + 200) - 100;
            const waveHeight = canvas.height * 0.6; // Vertical distance active in the wipe

            const getOpacity = (y) => {
                const dist = Math.abs(y - waveCenterY);
                if (dist > waveHeight / 2) return 0;
                // Cosine fade curve peaks around the center of the wipe
                const intensity = Math.cos((dist / (waveHeight / 2)) * (Math.PI / 2));
                return intensity * 0.3; // Limit the max opacity of the grid to a light grey visually
            };

            ctx.lineWidth = 1;

            // Draw horizontal rows one by one, uniformly styled across their width
            for (let i = 0; i <= rows; i++) {
                const y = i * cellHeight;
                const alpha = getOpacity(y);
                if (alpha > 0.01) {
                    ctx.beginPath();
                    ctx.strokeStyle = `rgba(200, 200, 200, ${alpha})`;
                    ctx.moveTo(0, y);
                    ctx.lineTo(canvas.width, y);
                    ctx.stroke();
                }
            }

            // Draw vertical columns. Because opacity must fade along the line's Y-axis, use a linear gradient
            const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
            let hasVisibleGradient = false;
            for (let step = 0; step <= 20; step++) {
                const y = (step / 20) * canvas.height;
                const alpha = getOpacity(y);
                grad.addColorStop(step / 20, `rgba(200, 200, 200, ${alpha})`);
                if (alpha > 0.01) hasVisibleGradient = true;
            }

            if (hasVisibleGradient) {
                ctx.beginPath();
                ctx.strokeStyle = grad;
                for (let i = 0; i <= cols; i++) {
                    const x = i * cellWidth;
                    ctx.moveTo(x, 0);
                    ctx.lineTo(x, canvas.height);
                }
                ctx.stroke();
            }
        }
    }

    function draw() {
        const points = Array.from(container.querySelectorAll('.dataexplorer_loader_scatter-point'));
        if (points.length === 0) {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', resize);
            if (canvas && canvas.parentNode) canvas.remove();
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 1; // Reset alpha each frame
        const containerRect = container.getBoundingClientRect();
        
        const now = Date.now();

        // 1. Reveal Grid Background periodically
        drawGridAnimation(now);
        
        // --- Manage Wave state ---
        if (!isWaveActive && now > nextWaveTime) {
            isWaveActive = true;
            waveStartTime = now;
            // Wave length is between WAVE_DURATION_MIN and WAVE_DURATION_MAX
            waveDuration = WAVE_DURATION_MIN + (Math.random() * (WAVE_DURATION_MAX - WAVE_DURATION_MIN));
            // Randomly pick direction: left-to-right or right-to-left
            waveDirection = Math.random() > 0.5 ? 1 : -1;
            
            if (pendingSecondaryWave) {
                // Secondary waves are half normal intensity
                currentWaveIntensity = WAVE_INTENSITY / 2;
                pendingSecondaryWave = false; // Consume 
            } else {
                // Normal wave intensity
                currentWaveIntensity = WAVE_INTENSITY;
                // Chance to queue up an immediate secondary wave when this one concludes
                if (Math.random() < WAVE_SECONDARY_CHANCE) {
                    pendingSecondaryWave = true;
                }
            }
        }

        let currentWaveProgress = 0;
        if (isWaveActive) {
            currentWaveProgress = (now - waveStartTime) / waveDuration;
            if (currentWaveProgress >= 1) {
                isWaveActive = false;
                waveX = -1000;
                
                if (pendingSecondaryWave) {
                    // Fire secondary wave extremely quickly
                    nextWaveTime = now + 100;
                } else {
                    // Wait for next wave
                    nextWaveTime = now + WAVE_WAIT_MIN + (Math.random() * (WAVE_WAIT_MAX - WAVE_WAIT_MIN));
                }
            } else {
                // Sweep wave position progressively across the canvas width
                if (waveDirection === 1) {
                    waveX = -waveBandWidth + (currentWaveProgress * (canvas.width + waveBandWidth * 2));
                } else {
                    waveX = canvas.width + waveBandWidth - (currentWaveProgress * (canvas.width + waveBandWidth * 2));
                }
            }
        }

        // Global clear fading
        let globalAlpha = 1;
        if (isClearing) {
            let clearProgress = (now - clearStartTime) / CLEAR_DURATION;
            if (clearProgress >= 1) {
                // Done clearing entirely
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                cancelAnimationFrame(animationId);
                if (canvas && canvas.parentNode) canvas.remove();
                if (container) container.style.display = 'none'; // Hide the backing DOM elements (SVG, raw dots)
                return; 
            }
            globalAlpha = 1 - clearProgress;
            ctx.globalAlpha = globalAlpha;
            if (container) container.style.opacity = globalAlpha; // Fade out the entire container simultaneously
        }

        const dotsInfo = points.map((p, i) => {
            const rect = p.getBoundingClientRect();
            let opacity = 1;
            if (window.getComputedStyle) {
                const comp = window.getComputedStyle(p);
                opacity = parseFloat(comp.opacity);
                if (isNaN(opacity)) opacity = 1;
            }
            
            let bgColor = p.style.backgroundColor;
            if (!bgColor && window.getComputedStyle) {
                bgColor = window.getComputedStyle(p).backgroundColor;
            }
            
            let r = 150, g = 150, b = 150;
            const rgbMatch = (bgColor || '').match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
            if (rgbMatch) { r = parseInt(rgbMatch[1]); g = parseInt(rgbMatch[2]); b = parseInt(rgbMatch[3]); }

            const cx = rect.left - containerRect.left + (rect.width / 2);
            const cy = rect.top - containerRect.top + (rect.height / 2);
            
            // Calculate wave excitement intensity for this dot specifically based on its proximity to waveX
            let waveIntensity = 0;
            if (isWaveActive) {
                const distFromWaveCenter = Math.abs(cx - waveX);
                if (distFromWaveCenter < waveBandWidth / 2) {
                    // Cosine curve intensity logic: 1.0 bright at the pure center of the wave, softly fading to 0 at the edges
                    waveIntensity = Math.cos((distFromWaveCenter / (waveBandWidth / 2)) * (Math.PI / 2)) * currentWaveIntensity;
                }
            }
            
            // Reveal hidden dots & excite them incrementally: push baseline opacity up towards 1 as the intensity rises over them.
            // When intensity is 0, it falls backward to naturally adhere to the DOM's native computed CSS opacity limit.
            const targetedOpacity = Math.min(1, opacity + (waveIntensity * Math.max(0.8 - opacity, 0)));
            const renderedOpacity = Math.max(0.01, targetedOpacity);
            
            // Override physical DOM dots and draw everything via canvas
            p.style.opacity = '0.01';

            return {
                x: cx,
                y: cy,
                r, g, b, color: bgColor,
                opacity: renderedOpacity,
                excitement: waveIntensity
            };
        });

        // Draw network connections
        for (let i = 0; i < dotsInfo.length; i++) {
            const p1 = dotsInfo[i];
            if (p1.opacity <= 0.05 && p1.excitement <= 0.01) continue;
            
            if (p1.opacity > 0) {
                ctx.beginPath();
                // Normal dots = 2px. Excited wave dots swell slightly (up to 4.5px radius based on severity)
                const radius = 2 + (p1.excitement * 2.5);
                ctx.arc(p1.x, p1.y, radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p1.r}, ${p1.g}, ${p1.b}, ${p1.opacity})`;
                ctx.fill();
            }

            // Sort remaining neighbors by their closest distance to us
            const neighbors = [...dotsInfo];
            neighbors.splice(i, 1);
            neighbors.sort((a, b) => {
                return Math.hypot(a.x - p1.x, a.y - p1.y) - Math.hypot(b.x - p1.x, b.y - p1.y);
            });

            // Connect lines up to top closest points
            for (let j = 0; j < Math.min(4, neighbors.length); j++) {
                const p2 = neighbors[j];
                const lineExcitement = Math.max(p1.excitement, p2.excitement);

                if (p2.opacity <= 0.05 && lineExcitement <= 0.01) continue;

                // Typical draw distance is 200px, expand networking distance out to 350 dynamically when the wave hits
                const maxDist = 200 + (150 * lineExcitement); 
                const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
                if (dist > maxDist) continue;

                const distFactor = Math.max(0, 1 - (dist / maxDist));
                // Boost line opacity intensely when bridging via wave excitement
                const lineOpacity = distFactor * p1.opacity * p2.opacity * (0.4 + (0.6 * lineExcitement));

                if (lineOpacity <= 0.01) continue;

                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);

                try {
                    const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
                    grad.addColorStop(0, `rgba(${p1.r}, ${p1.g}, ${p1.b}, ${lineOpacity})`);
                    grad.addColorStop(1, `rgba(${p2.r}, ${p2.g}, ${p2.b}, ${lineOpacity})`);
                    ctx.strokeStyle = grad;
                } catch(e) {
                    ctx.strokeStyle = `rgba(150, 150, 150, ${lineOpacity})`;
                }

                // Thicken laser lines seamlessly while hit by the wave
                ctx.lineWidth = 1 + (2.0 * lineExcitement);
                ctx.stroke();
            }
        }

        animationId = requestAnimationFrame(draw);
    }
    
    // Add window export for easy console triggering
    window.startScatterNetwork = startScatterNetwork;

    draw();
}